import {ChangeDetectionStrategy, Component, computed, input, model, output} from '@angular/core';
import {
  AcceptCommercialRelease,
  CommercialReleaseSummary,
  Modality,
  RejectCommercialRelease,
  TaskMessageStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AppContext} from "../../../app-context";
import {commercialReleaseDeclarationStatusToTaskMessageStatus, terminalOrganisationName} from "../../../commercial-release/commercial-release.utils";
import {DeclarationMessageStatus} from "../../../new-portvisit/visit-overview/visit-overview-item/visit-overview.utils";
import {CheckboxSelectionState, openEditModal, sendCommand} from "../../../common/utils";
import {
  CommercialReleaseModalComponent,
  CommercialReleaseModalComponentData
} from "../commercial-release-modal/commercial-release-modal.component";
import {PortvisitUtils} from "../../../refdata/portvisit-utils";

@Component({
  selector: 'app-commercial-release-overview-item',
  templateUrl: './commercial-release-overview-item.component.html',
  styleUrls: ['./commercial-release-overview-item.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommercialReleaseOverviewItemComponent {
  appContext = AppContext;
  terminalOrganisationName = terminalOrganisationName;
  utils = PortvisitUtils;

  releaseSummary = model<CommercialReleaseSummary>(undefined, {alias: 'summary'});
  term = input<string>();
  // Selection triplet + fields, same shape as master-consignment-overview-item.component.ts.
  readonly selectable = input<boolean>();
  readonly selected = input<boolean>(false);
  readonly selectionToggled = output<void>();
  readonly checkboxSelectionState = CheckboxSelectionState;
  selectionWidth: number = 40;

  // Ported verbatim from CommercialReleaseAdminComponent.rejectionReasons default.
  rejectionReasons = "Rejected by Portbase";

  // Founder feedback (reverses an earlier decision): the status column must match consignments'
  // actual pattern - `app-status-indicator` with a click-triggered Bootstrap popover, not a bare
  // `app-task-message-status`. A commercial release only has ONE status bucket, but
  // `app-status-indicator` accepts an array of any length (including 1), so a single-element
  // `DeclarationMessageStatus[]` is the correct/honest use of the shared component here. The reject
  // reason is carried in `messages` (same as consignments' `getRenStatus()`/`getMidStatus()`
  // `taskStatus === TaskMessageStatus.REJECTED ? [reason] : []` pattern) so it shows up as a message
  // line inside the popover instead of an inline `<span>` under the label.
  releaseMessageStatus = computed<DeclarationMessageStatus[]>(() => {
    const status = this.releaseSummary()?.releaseStatus;
    const taskStatus = this.releaseTaskStatus();
    return [{
      name: "Release",
      taskStatus,
      messages: (taskStatus === TaskMessageStatus.REJECTED && status?.rejectReasons) ? [status.rejectReasons] : [],
    }];
  });

  releaseTaskStatus = computed<TaskMessageStatus>(() => {
    const status = this.releaseSummary()?.releaseStatus;
    return commercialReleaseDeclarationStatusToTaskMessageStatus(status?.status);
  })

  // Release-to-party icon depends on the transport modality: road (truck, also the fallback for
  // unset/unknown modalities), rail (train), or barge/sea (ship - there is no distinct barge glyph
  // in this icon set, ship is the closest match for a waterborne vessel).
  releaseToPartyIconClass = computed<string>(() => {
    switch (this.releaseSummary()?.releaseData?.releaseModality) {
      case Modality.rail:
        return 'fa-train me-1';
      case Modality.barge:
      case Modality.sea:
        return 'fa-ship me-1';
      default:
        return 'fa-truck me-1';
    }
  });

  openDetails = () => {
    openEditModal(CommercialReleaseModalComponent, <CommercialReleaseModalComponentData>{release: this.releaseSummary()}, {
      warnOnClose: true
    });
  }

  // Ported verbatim from CommercialReleaseAdminComponent.adminAllowedToManipulateCR().
  adminAllowedToManipulateCR() {
    // in production, the terminal communication goes wrong; this is not solved by manipulating the CR, it only SEEMS to solve it
    // On any other environment, even KT, there is no sensible communication with terminals, so the actions are safe
    if (AppContext.isProduction()) return false;

    return this.releaseSummary()?.releaseStatus?.status === 'DECLARED';
  }

  // Ported verbatim from CommercialReleaseAdminComponent.acceptRelease().
  acceptRelease() {
    sendCommand('com.portbase.bezoekschip.common.api.commercialrelease.AcceptCommercialRelease',
      <AcceptCommercialRelease>{id: this.releaseSummary().id},
      () => AppContext.registerSuccess('The release has been accepted manually.'))
  }

  // Ported verbatim from CommercialReleaseAdminComponent.rejectRelease().
  rejectRelease() {
    sendCommand('com.portbase.bezoekschip.common.api.commercialrelease.RejectCommercialRelease',
      <RejectCommercialRelease>{id: this.releaseSummary().id, reasons: this.rejectionReasons},
      () => AppContext.registerSuccess('The release has been rejected manually.'))
  }
}
